import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { startWith, takeUntil } from 'rxjs/operators';
import { DateTimezoneService } from '@services/date-timezone.service';
import { Subject } from 'rxjs';
import { BsDatepickerConfig, BsDatepickerViewMode } from 'ngx-bootstrap/datepicker';

@Component({
  selector: 'date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
})
export class DatePickerComponent implements OnInit, OnDestroy {
  @Input() public wrapperFc: UntypedFormControl | AbstractControl;
  @Input() public fc: UntypedFormControl | AbstractControl;
  @Input() public errors: IErrorForm;
  @Input() public label: string;
  @Input() public showHelper: boolean = true;
  @Input() public helper: string;
  @Input() public placeholder: string;
  @Input() public id: string | number;
  @Input() public name: string | number;
  @Input() public maxDate: string;
  @Input() public minDate: string;
  @Input() public minMode: BsDatepickerViewMode;
  @Input() public dateInputFormat: string;
  @Input() public errorsOverrideParams: any;
  @Input() public showCurrentDate: boolean = false;
  @Input() public required: boolean = false;
  @Input() public isShowDisabled: boolean = false;
  @Input() public labelClass: string;
  @Input() public isWithIcon: boolean = false;

  public dateValue: string;
  public bsConfig: BsDatepickerConfig;

  private isWasClicked: boolean = false;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private dateTimezoneService: DateTimezoneService) {}

  public ngOnInit(): void {
    this.fc.valueChanges.pipe(startWith(this.fc.value), takeUntil(this.destroy$)).subscribe((value: string) => {
      this.dateValue = this.dateTimezoneService.formatDateWithoutTime(value);
    });

    this.bsConfig = {
      ...new BsDatepickerConfig(),
      ...(!!this.minMode ? { minMode: this.minMode } : {}),
      ...(!!this.dateInputFormat ? { dateInputFormat: this.dateInputFormat } : {}),
      containerClass: 'custom-theme',
      showWeekNumbers: false,
      adaptivePosition: true,
      isAnimated: true,
      customTodayClass: this.showCurrentDate ? 'today' : null,
    };
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  public get minDateFormatted(): Date {
    if (this.minDate) {
      return this.dateTimezoneService.formatModelValue(this.minDate);
    }
    return undefined;
  }

  public get maxDateFormatted(): Date {
    if (this.maxDate) {
      return this.dateTimezoneService.formatModelValue(this.maxDate);
    }
    return undefined;
  }

  public onClickDate(element: HTMLElement): void {
    if (!this.isWasClicked) {
      element.blur();
      this.fc.markAsTouched();
    }
    this.isWasClicked = true;

    if (this.fc.untouched) {
      this.fc.markAsTouched();
    }
  }

  public onHidden(): void {
    this.isWasClicked = false;
  }

  public onModelChange(value: Date | string | null) {
    this.dateValue = value as string;
    // #5402
    this.fc.patchValue(this.dateTimezoneService.formatViewValue(value as Date, false));
    // #12298: a user pick/clear must mark the control dirty (patchValue alone does not),
    // so the dirty-guards in the ES edit forms detect the edit.
    this.fc.markAsDirty();
    if (this.fc.untouched) {
      this.fc.markAsTouched();
    }
  }
}
